/**
 * Morphological intent matching (D36): inflected verbs reach their base form.
 *
 * @remarks
 * Players type "kicking the door", "smashed the lock", "he opens it" — the
 * same verb wearing English's regular inflections. Enumerating every form as
 * an alias is the enumeration trap this decision exists to escape, so the
 * shapes are stripped MECHANICALLY instead: `-s`, `-es`, `-ed`, `-ing`, plus
 * the doubled-consonant ("kicking" → "kick", "stopped" → "stop") and dropped-e
 * ("moving" → "move") spellings.
 *
 * Deliberately conservative: it proposes candidate base forms and the CALLER
 * accepts only one the vocabulary already knows. Nothing is invented, no
 * dictionary is consulted, and an exact match is always preferred — this runs
 * only after exact and alias matching have both failed.
 *
 * @packageDocumentation
 */

// cspell:ignore kickk aeiou -- the doubled-consonant example below, and the vowel class in the pattern

/** Suffixes stripped to reach a base form, longest first (order decides nothing — every candidate is tried). */
const INFLECTION_SUFFIXES = ['ing', 'ed', 'es', 's'] as const;

/** Vowels, for the doubled-consonant test ("kicking" → "kick", never "kickk"). */
const VOWEL_PATTERN = /[aeiou]/u;

/**
 * Candidate base forms for one inflected word, in preference order.
 *
 * @param word - The folded surface word
 * @returns Zero or more candidate base forms (empty when nothing plausible strips)
 */
export const proposeVerbBaseForms = (word: string): readonly string[] => {
	const candidates: string[] = [];

	for (const suffix of INFLECTION_SUFFIXES) {
		if (!word.endsWith(suffix) || word.length <= suffix.length + 1) {
			continue;
		}

		const stem = word.slice(0, -suffix.length);

		candidates.push(stem);

		// "moving" → "move", "closed" → "close": English drops the silent e
		// before a vowel-initial suffix.
		if (suffix === 'ing' || suffix === 'ed') {
			candidates.push(`${stem}e`);
		}

		// "stopped" → "stop", "running" → "run": the final consonant doubles
		// after a short vowel. Undo it, never invent it.
		const lastCharacter = stem.at(-1);
		const priorCharacter = stem.at(-2);

		if (
			lastCharacter !== undefined &&
			priorCharacter !== undefined &&
			lastCharacter === priorCharacter &&
			!VOWEL_PATTERN.test(lastCharacter)
		) {
			candidates.push(stem.slice(0, -1));
		}
	}

	return [...new Set(candidates)];
};
